/**
 * video-carousel.js
 * Video Carousel Web Component — Improved
 *
 * IMPROVEMENTS:
 * 1. No cloning — pure index-based modulo wrapping
 * 2. Directional slide transitions (left ↔ right) via CSS translate animation
 * 3. Dynamic height — derived from slide aspect ratio at runtime
 * 4. Bounded navigation — won't go beyond available slides at the edges
 *
 * LAYOUT:
 * - Desktop (≥990px): 5 slides visible
 * - Tablet (750–989px): 3 slides visible
 * - Mobile (<750px): 1 slide visible
 */

class VideoCarousel extends HTMLElement {

  static get observedAttributes() {
    return ['data-autoplay', 'data-autoplay-speed', 'data-autoplay-center'];
  }

  static get DESKTOP_BP()    { return 990; }
  static get TABLET_BP()     { return 750; }
  static get ANIM_DURATION() { return 700; } // ms — must match CSS transition

  /* ─── Lifecycle ─────────────────────────────────────────────── */

  connectedCallback() {
    this.track   = this.querySelector('.video-carousel-track');
    this.wrapper = this.querySelector('.video-carousel-wrapper');
    this.slides  = Array.from(this.querySelectorAll('.video-carousel-slide'));
    this.dots    = this.querySelectorAll('.video-carousel-dot');
    this.btnPrev = this.querySelector('.video-carousel-nav-button.prev');
    this.btnNext = this.querySelector('.video-carousel-nav-button.next');

    if (!this.track || !this.slides.length) return;

    this._isAnimating = false;
    this.autoplayTimer = null;
    this.touchStartX   = 0;
    this.resizeTimer   = null;

    this.state = {
      currentIndex:    0,
      total:           this.slides.length,
      isDesktop:       window.innerWidth >= VideoCarousel.DESKTOP_BP,
      isTablet:        window.innerWidth >= VideoCarousel.TABLET_BP && window.innerWidth < VideoCarousel.DESKTOP_BP,
      currentVideo:    null,
      autoplayEnabled: this.dataset.autoplay       === 'true',
      autoplaySpeed:   parseInt(this.dataset.autoplaySpeed)  || 5000,
      autoplayCenter:  this.dataset.autoplayCenter === 'true',
    };

    // Inject transition CSS once
    this._injectTransitionStyle();

    // Start collecting natural video dimensions async (ratios arrive via loadedmetadata)
    this._initVideoMetadata();

    this.bindEvents();
    this.render(null);       // initial render — uses fallback ratio until metadata arrives
    this.updateDots();
    this.updateNavButtons();

    if (this.state.autoplayEnabled) this.startAutoplay();
    if (this.state.autoplayCenter)  setTimeout(() => this.playCenterVideo(), 500);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.stopAllVideos();
    this._prevWindow = null;
    window.removeEventListener('resize', this._onResize);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (!this.state || oldVal === newVal) return;
    if (name === 'data-autoplay') {
      this.state.autoplayEnabled = newVal === 'true';
      newVal === 'true' ? this.startAutoplay() : this.stopAutoplay();
    }
    if (name === 'data-autoplay-speed') {
      this.state.autoplaySpeed = parseInt(newVal) || 5000;
      if (this.autoplayTimer) { this.stopAutoplay(); this.startAutoplay(); }
    }
    if (name === 'data-autoplay-center') {
      this.state.autoplayCenter = newVal === 'true';
    }
  }

  /* ─── Style injection ───────────────────────────────────────── */

  _injectTransitionStyle() {
    if (document.getElementById('vc-slide-style')) return;
    const dur = VideoCarousel.ANIM_DURATION;
    const style = document.createElement('style');
    style.id = 'vc-slide-style';
    style.textContent = `
      .video-carousel-slide {
        position: absolute;
        top: 0;
        transition:
          left   ${dur}ms cubic-bezier(0.4, 0, 0.2, 1),
          opacity ${dur}ms ease,
          transform ${dur}ms cubic-bezier(0.4, 0, 0.2, 1);
        will-change: left, opacity, transform;
      }
      .video-carousel-track {
        position: relative;
        overflow: hidden;
      }
    `;
    document.head.appendChild(style);
  }

  /* ─── Helpers ───────────────────────────────────────────────── */

  mod(n, m) { return ((n % m) + m) % m; }

  getVisibleCount() {
    const { total } = this.state;
    let count = this.state.isDesktop ? 5 : this.state.isTablet ? 3 : 1;
    // IMPROVEMENT 4: never show more slots than we have slides
    return Math.min(count, total);
  }

  /** Center slot position within the visible window */
  getCenterOffset(visibleCount) {
    return Math.floor(visibleCount / 2);
  }

  /**
   * Returns ordered array of { slideIndex, position } for visible window.
   * IMPROVEMENT 4: wrapping only happens when total > visibleCount.
   */
  getSlotAssignments() {
    const { currentIndex, total } = this.state;
    const visibleCount = this.getVisibleCount();
    const centerOffset = Math.floor(visibleCount / 2);
    const assignments = [];

    const shouldWrap = total >= visibleCount;

    for (let pos = 0; pos < visibleCount; pos++) {
      const rawIndex = currentIndex - centerOffset + pos;

      let slideIndex;

      if (shouldWrap) {
        // Pure circular math
        slideIndex = ((rawIndex % total) + total) % total;
      } else {
        // True bounded mode
        slideIndex = Math.max(0, Math.min(rawIndex, total - 1));
      }

      assignments.push({
        slideIndex,
        position: pos
      });
    }

    return assignments;
  }

  /* ─── Dynamic height from average video aspect ratios ──────── */

  /**
   * Called once on connectedCallback.
   * Listens for loadedmetadata on every video element, collects their
   * natural aspect ratios (height / width), and recalculates track height
   * each time a new ratio arrives so the carousel refines itself as videos load.
   */
  _initVideoMetadata() {
    // Map of slideIndex → aspect ratio (h/w), populated as metadata loads
    this._videoRatios = new Map();

    this.slides.forEach((slide, idx) => {
      const video = slide.querySelector('.video-carousel-video');
      if (!video) return;

      const onMeta = () => {
        if (video.videoWidth && video.videoHeight) {
          this._videoRatios.set(idx, video.videoHeight / video.videoWidth);
          this._updateHeight(); // recalculate now that we have one more ratio
        }
      };

      if (video.readyState >= 1) {
        // Metadata already available (e.g. cached)
        onMeta();
      } else {
        video.addEventListener('loadedmetadata', onMeta, { once: true });
      }
    });
  }

  /**
   * Computes average aspect ratio across all videos whose metadata has loaded,
   * then applies height = slideWidth × avgRatio to the track.
   * Falls back to 1.5 (2:3 portrait) if no metadata is available yet.
   */
  _updateHeight() {
    const visibleCount = this.getVisibleCount();
    const gap = parseInt(getComputedStyle(this.track).gap) || 20;
    const wrapperWidth = this.wrapper.getBoundingClientRect().width;

    if (!wrapperWidth) return;

    const slideWidth = (wrapperWidth - gap * (visibleCount - 1)) / visibleCount;

    // Average the collected ratios, or fallback to 1.1
    let avgRatio = 1.1;
    if (this._videoRatios && this._videoRatios.size > 0) {
      const sum = [...this._videoRatios.values()].reduce((a, b) => a + b, 0);
      avgRatio = sum / this._videoRatios.size;
    }

    // Determine device-specific offset
    let offset = 40; // default desktop
    const vw = window.innerWidth;

    if (vw <= 480) {
      offset = 75;
    } else if (vw <= 750) {
      offset = 100;
    } else if (vw <= 990) {
      offset = 50;
    }

    this.track.style.height = `${slideWidth * avgRatio - offset}px`;
  }

  /* ─── Render ────────────────────────────────────────────────── */

  /**
   * Circular window render.
   *
   * Layout (5 visible, centerOffset = 2):
   *   currentIndex = 0  →  [3] [4] [0] [1] [2]
   *   positions         →   0   1   2   3   4
   *
   * Every slide in the visible window gets an explicit `left` pixel value.
   * CSS transition animates them all sliding together left or right.
   *
   * Slides entering the window are pre-positioned just off-screen on the
   * correct side (no transition), then their target left is applied so the
   * transition fires naturally on the next paint.
   *
   * Slides leaving the window animate off-screen, then are hidden after
   * the transition completes.
   *
   * direction = 'next' | 'prev' | null (null = instant, no animation)
   */
  render(direction) {
    const { currentIndex } = this.state;
    const visibleCount = this.getVisibleCount();
    const gap          = parseInt(getComputedStyle(this.track).gap) || 20;
    const wrapperWidth = this.wrapper.getBoundingClientRect().width;
    const slideWidth   = (wrapperWidth - gap * (visibleCount - 1)) / visibleCount;
    const unit         = slideWidth + gap; // one slot's worth of pixels

    this._updateHeight();

    // Build the new window: Map<slideIndex → targetLeft>
    const newWindow = new Map();
    const assignments = this.getSlotAssignments();
    assignments.forEach(({ slideIndex, position }) => {
      newWindow.set(slideIndex, position * unit);
    });

    // Retrieve the previous window stored on last render
    const prevWindow = this._prevWindow || new Map();

    this.slides.forEach((slide, idx) => {
      const inNew  = newWindow.has(idx);
      const inPrev = prevWindow.has(idx);

      if (!direction) {
        /* ── Initial / instant render (no animation) ────────────── */
        if (inNew) {
          slide.style.transition    = 'none';
          slide.style.left          = `${newWindow.get(idx)}px`;
          slide.style.width         = `${slideWidth}px`;
          slide.style.opacity       = '1';
          slide.style.pointerEvents = 'auto';
          slide.classList.toggle('active', idx === currentIndex);
          void slide.offsetWidth;
          slide.style.transition = '';
        } else {
          // Not in window — park it off-screen to the left (invisible, out of the way)
          slide.style.transition    = 'none';
          slide.style.left          = `${-unit}px`;
          slide.style.opacity       = '0';
          slide.style.pointerEvents = 'none';
          slide.classList.remove('active');
          void slide.offsetWidth;
          slide.style.transition = '';
        }
        return;
      }

      /* ── Animated render ─────────────────────────────────────── */

      if (inNew && inPrev) {
        /* Slide was visible before and stays visible — just move it */
        const oldLeft = prevWindow.get(idx);
        const newLeft = newWindow.get(idx);

        const isWrapNext =
          direction === 'next' &&
          oldLeft === 0 &&
          newLeft === (visibleCount - 1) * unit;

        const isWrapPrev =
          direction === 'prev' &&
          oldLeft === (visibleCount - 1) * unit &&
          newLeft === 0;

        slide.style.width = `${slideWidth}px`;
        slide.style.pointerEvents = 'auto';
        slide.classList.toggle('active', idx === currentIndex);

        if (isWrapNext || isWrapPrev) {
          // 🔥 Fade out
          slide.style.transition = 'opacity 200ms ease';
          slide.style.opacity = '0';

          setTimeout(() => {
            // Jump instantly (no movement animation)
            slide.style.transition = 'none';
            slide.style.left = `${newLeft}px`;

            void slide.offsetWidth;

            // Fade back in
            slide.style.transition = 'opacity 200ms ease';
            slide.style.opacity = '1';
          }, 200);

        } else {
          // Normal slide animation
          slide.style.transition = '';
          slide.style.left = `${newLeft}px`;
          slide.style.opacity = '1';
        }
      } else if (inNew && !inPrev) {
        /* Slide is entering — pre-position off-screen on the incoming side */
        const entryLeft = direction === 'next'
          ? wrapperWidth + gap   // enters from right
          : -unit;               // enters from left

        slide.style.transition    = 'none';
        slide.style.left          = `${entryLeft}px`;
        slide.style.width         = `${slideWidth}px`;
        slide.style.opacity       = '1';
        slide.style.pointerEvents = 'auto';
        slide.classList.toggle('active', idx === currentIndex);
        void slide.offsetWidth; // force reflow before re-enabling transition

        slide.style.transition = ''; // restore CSS transition
        slide.style.left       = `${newWindow.get(idx)}px`; // animate to target

      } else if (!inNew && inPrev) {
        /* Slide is leaving — animate it off-screen on the outgoing side */
        const exitLeft = direction === 'next'
          ? -unit                // exits to left
          : wrapperWidth + gap;  // exits to right

        slide.style.left    = `${exitLeft}px`;
        slide.style.opacity = '0';

        // After animation: fully remove from interaction
        setTimeout(() => {
          // Guard: don't hide if it somehow re-entered the window
          if (!this._prevWindow || !this._prevWindow.has(idx)) return;
          slide.style.pointerEvents = 'none';
          slide.classList.remove('active');
        }, VideoCarousel.ANIM_DURATION);

      } else {
        /* Slide is off-screen on both sides — keep it hidden, no transition */
        slide.style.transition    = 'none';
        slide.style.opacity       = '0';
        slide.style.pointerEvents = 'none';
        slide.classList.remove('active');
        void slide.offsetWidth;
        slide.style.transition = '';
      }
    });

    // Save current window for next render's diff
    this._prevWindow = new Map(newWindow);
  }

  /* ─── Dots ──────────────────────────────────────────────────── */

  updateDots() {
    this.dots.forEach((dot, i) => dot.classList.toggle('active', i === this.state.currentIndex));
  }

  /* ─── Nav button state (IMPROVEMENT 4) ─────────────────────── */

  updateNavButtons() {
    const { currentIndex, total } = this.state;
    const visibleCount = this.getVisibleCount();
    const canWrap = total > visibleCount;

    if (this.btnPrev) {
      this.btnPrev.disabled = !canWrap && currentIndex === 0;
      this.btnPrev.style.opacity = this.btnPrev.disabled ? '0.3' : '';
    }
    if (this.btnNext) {
      const centerOffset = this.getCenterOffset(visibleCount);
      const lastCenter   = total - 1 - (visibleCount - 1 - centerOffset);
      this.btnNext.disabled = !canWrap && currentIndex >= lastCenter;
      this.btnNext.style.opacity = this.btnNext.disabled ? '0.3' : '';
    }
  }

  /* ─── Navigation ────────────────────────────────────────────── */

  _canNavigate(dir) {
    const { currentIndex, total } = this.state;
    const visibleCount  = this.getVisibleCount();
    const canWrap       = total > visibleCount;
    if (canWrap) return true; // infinite scroll always allowed
    if (dir === 'prev') return currentIndex > 0;
    if (dir === 'next') {
      const centerOffset = this.getCenterOffset(visibleCount);
      const lastCenter   = total - 1 - (visibleCount - 1 - centerOffset);
      return currentIndex < lastCenter;
    }
    return true;
  }

  next() {
    if (this._isAnimating || !this._canNavigate('next')) return;
    this._isAnimating = true;
    this.stopAllVideos();
    this.state.currentIndex = this.mod(this.state.currentIndex + 1, this.state.total);
    this.render('next');
    this.updateDots();
    this.updateNavButtons();
    if (this.state.autoplayCenter) setTimeout(() => this.playCenterVideo(), 100);
    setTimeout(() => { this._isAnimating = false; }, VideoCarousel.ANIM_DURATION);
  }

  prev() {
    if (this._isAnimating || !this._canNavigate('prev')) return;
    this._isAnimating = true;
    this.stopAllVideos();
    this.state.currentIndex = this.mod(this.state.currentIndex - 1, this.state.total);
    this.render('prev');
    this.updateDots();
    this.updateNavButtons();
    if (this.state.autoplayCenter) setTimeout(() => this.playCenterVideo(), 100);
    setTimeout(() => { this._isAnimating = false; }, VideoCarousel.ANIM_DURATION);
  }

  goToSlide(index) {
    if (this._isAnimating || index === this.state.currentIndex) return;
    this._isAnimating = true;
    // Shortest circular path determines slide direction
    const { currentIndex, total } = this.state;
    const fwd  = this.mod(index - currentIndex, total); // steps going next
    const back = this.mod(currentIndex - index, total); // steps going prev
    const dir  = fwd <= back ? 'next' : 'prev';
    this.stopAllVideos();
    this.state.currentIndex = index;
    this.render(dir);
    this.updateDots();
    this.updateNavButtons();
    if (this.state.autoplayCenter) setTimeout(() => this.playCenterVideo(), 100);
    setTimeout(() => { this._isAnimating = false; }, VideoCarousel.ANIM_DURATION);
  }

  /* ─── Video ─────────────────────────────────────────────────── */

  playCenterVideo() {
    if (!this.state.autoplayCenter) return;
    const centerSlide = this.slides[this.state.currentIndex];
    const video = centerSlide?.querySelector('.video-carousel-video');
    if (!video) return;
    if (!centerSlide.classList.contains('playing')) {
      this.playVideo(centerSlide, video);
    }
  }

  playVideo(slide, video) {
    this.stopAllVideos();
    video.play().catch(() => {});
    video.classList.add('playing');
    slide.classList.add('playing');
    this.state.currentVideo = { slide, element: video };
    this.stopAutoplay();
  }

  stopVideo(slide, video) {
    video.pause();
    video.currentTime = 0;
    video.classList.remove('playing');
    slide.classList.remove('playing');
    this.state.currentVideo = null;
    if (this.state.autoplayEnabled) this.startAutoplay();
  }

  stopAllVideos() {
    this.slides.forEach(slide => {
      const video = slide.querySelector('.video-carousel-video');
      if (video && slide.classList.contains('playing')) this.stopVideo(slide, video);
    });
  }

  /* ─── Autoplay ──────────────────────────────────────────────── */

  startAutoplay() {
    this.stopAutoplay();
    if (this.state.autoplayEnabled) {
      this.autoplayTimer = setInterval(() => this.next(), this.state.autoplaySpeed);
    }
  }

  stopAutoplay() {
    if (this.autoplayTimer) { clearInterval(this.autoplayTimer); this.autoplayTimer = null; }
  }

  /* ─── Events ────────────────────────────────────────────────── */

  bindEvents() {
    if (this.btnPrev) this.btnPrev.addEventListener('click', () => this.prev());
    if (this.btnNext) this.btnNext.addEventListener('click', () => this.next());

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goToSlide(i)));

    this.slides.forEach((slide, idx) => {
      const video      = slide.querySelector('.video-carousel-video');
      const playButton = slide.querySelector('.video-carousel-play-button');

      const handleClick = () => {
        if (idx === this.state.currentIndex) {
          if (!video) return;
          slide.classList.contains('playing')
            ? this.stopVideo(slide, video)
            : this.playVideo(slide, video);
        } else {
          this.goToSlide(idx);
        }
      };

      slide.addEventListener('click', handleClick);
      if (playButton) {
        playButton.addEventListener('click', (e) => { e.stopPropagation(); handleClick(); });
      }
    });

    // Touch swipe
    this.track.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.track.addEventListener('touchend', (e) => {
      const diff = this.touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > 50) diff > 0 ? this.next() : this.prev();
    });

    // Hover: pause/resume autoplay on desktop
    this.addEventListener('mouseenter', () => {
      if (window.innerWidth >= VideoCarousel.TABLET_BP) this.stopAutoplay();
    });
    this.addEventListener('mouseleave', () => {
      if (window.innerWidth >= VideoCarousel.TABLET_BP && this.state.autoplayEnabled) this.startAutoplay();
    });

    // Resize
    this._onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        const wasDesktop = this.state.isDesktop;
        const wasTablet  = this.state.isTablet;
        this.state.isDesktop = window.innerWidth >= VideoCarousel.DESKTOP_BP;
        this.state.isTablet  = window.innerWidth >= VideoCarousel.TABLET_BP && window.innerWidth < VideoCarousel.DESKTOP_BP;
        if (wasDesktop !== this.state.isDesktop || wasTablet !== this.state.isTablet) {
          this.render(null);
          this.updateNavButtons();
        } else {
          // Same breakpoint but window width changed — recalculate sizes
          this.render(null);
        }
      }, 150);
    };
    window.addEventListener('resize', this._onResize);
  }
}

/* ─── Register ─────────────────────────────────────────────────── */
if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', VideoCarousel);
}

/* ─── Shopify theme editor live reload ─────────────────────────── */
document.addEventListener('shopify:section:load', (e) => {
  e.target.querySelectorAll('video-carousel').forEach(el => {
    el.disconnectedCallback?.();
    el.connectedCallback();
  });
});